<template>
  <div>
    <table v-if="dataStore.pidjourneyeventcounts" class="vehicle-table">
      <thead class="event-table">
        <tr>
          <th>Event</th>
          <th>Count</th>
          <th></th>
          <th>Flow</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in sortedEventCounts" :key="item.name">
          <td :key="'name-' + item.name">{{ item.name }}</td>
          <td :key="'count-' + item.name">{{ item.count }}</td>
          <td :key="'show-' + item.name"><button @click="toggle(item.name)"
              :class="{ toggled: toggles[item.name] }">show</button></td>
          <td :key="'flow-' + item.name" style="text-align: center;">
            <div class="checkbox-wrapper">
              <input type="checkbox" v-model="orderToggles[item.name]" @change="rebuildOrderLayer()"
                title="Include in sequence flow" />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { inject, reactive, onBeforeUnmount, computed } from 'vue';

const dataStore = inject('datastore');

const props = defineProps({
  jguid: {
    type: String,
    required: true
  }
});

const sortedEventCounts = computed(() => {
  const counts = dataStore.pidjourneyeventcounts[props.jguid];
  if (!counts) return [];
  return Object.keys(counts).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())).map(key => ({
    name: key,
    count: counts[key]
  }));
});


const toggles = reactive({});
const orderToggles = reactive({});
let layers = {};
let layerBuilders = {};


function rebuildOrderLayer() {
  const layerName = 'order_flow_layer';

  // Remove existing
  if (layers[layerName]) {
    dataStore.removeLayer(layers[layerName]);
    delete layers[layerName];
  }

  // Gather selected types
  const selectedTypes = Object.keys(orderToggles).filter(k => orderToggles[k]);
  if (selectedTypes.length === 0) return;

  // Collect all events
  let allPoints = [];
  const journeyEvents = dataStore.pidjourneyevents[props.jguid];

  if (!journeyEvents) return;

  journeyEvents.forEach(evt => {
    if (selectedTypes.includes(evt.event)) {
      // Time Filter check
      const f = dataStore.timeFilters[props.jguid];
      if (f && f.enabled) {
        const t = Number(evt.time);
        if (t < f.start || t > f.end) return;
      }

      if (evt.gps && evt.gps.lat && evt.gps.lon) {
        allPoints.push({
          lat: evt.gps.lat,
          lon: evt.gps.lon,
          time: evt.time,
          type: evt.event
        });
      }
    }
  });

  // Sort by time
  allPoints.sort((a, b) => a.time - b.time);

  if (allPoints.length > 1) {
    let layer = L.layerGroup();
    const latlngs = allPoints.map(p => [p.lat, p.lon]);

    // Helper to interpolate colors: Blue (start) -> Magenta (mid) -> Red (end)
    function getColor(t) {
      const hue = 240 * (1 - t);
      return `hsl(${hue}, 100%, 50%)`;
    }

    // Draw segmented gradient line
    for (let i = 0; i < latlngs.length - 1; i++) {
      const start = latlngs[i];
      const end = latlngs[i + 1];
      const percent = i / (latlngs.length - 1);

      L.polyline([start, end], {
        color: getColor(percent),
        weight: 4,
        opacity: 0.8,
        interactive: false
      }).addTo(layer);
    }

    // Draw transparent single polyline for arrows
    const arrowPath = L.polyline(latlngs, {
      opacity: 0,
      interactive: false
    }).addTo(layer);

    // Arrows
    try {
      L.polylineDecorator(arrowPath, {
        patterns: [
          {
            repeat: '80px',
            symbol: L.Symbol.arrowHead({
              pixelSize: 12,
              polygon: true,
              pathOptions: { stroke: true, color: '#333', weight: 1, fill: true, fillColor: '#333', fillOpacity: 0.8 }
            })
          }
        ]
      }).addTo(layer);
    } catch (e) {
      console.warn("PolylineDecorator not available", e);
    }

    // Add start/end markers
    L.circleMarker(latlngs[0], { radius: 6, color: 'blue', fillColor: 'blue', fillOpacity: 1 })
      .bindTooltip("Start", { permanent: false }).addTo(layer);

    L.circleMarker(latlngs[latlngs.length - 1], { radius: 6, color: 'red', fillColor: 'red', fillOpacity: 1 })
      .bindTooltip("End", { permanent: false }).addTo(layer);

    layers[layerName] = layer;
    const wasEmpty = Object.keys(dataStore.activeLayers).length === 0;
    dataStore.addLayer(layer);
    // Explicitly suppressed auto-fit during rebuild
  }
}

// Watch filters
import { watch } from 'vue';
const timeFilter = computed(() => dataStore.timeFilters[props.jguid]);
watch(timeFilter, (newVal) => {
  console.log(`[OverviewWatcher] Filter changed for ${props.jguid}`, newVal);
  rebuildOrderLayer();
  // Also update individual event layers
  Object.keys(toggles).forEach(k => {
    if (toggles[k]) {
      hideLayer(k); showLayer(k, true);
    }
  });
}, { deep: true });

onBeforeUnmount(() => {
  hideAll()
});

function layerbuilder(name, fn) {
  layerBuilders[name] = fn;
}

function hideAll() {
  for (let name in layers) {
    hideLayer(name);
  }
}

function hideLayer(name) {
  if (layers[name]) {
    dataStore.removeLayer(layers[name]);
    delete layers[name];
  }
}

function showLayer(name, preventFit = false) {

  let eventName = name.split("_")[1];
  if (!eventName) {
    eventName = name
  }

  if (!layers[name]) {
    if (!layerBuilders[eventName]) {
      layerbuilder(eventName, genericEventBuilder(eventName, evtPopupText))
    }
    layers[name] = layerBuilders[eventName](name);
  }

  if (layers[name]) {
    const wasEmpty = Object.keys(dataStore.activeLayers).length === 0;
    dataStore.addLayer(layers[name]);
    if (wasEmpty && !preventFit) {
      dataStore.fitMapToContent();
    }
  }
}

function toggle(what) {
  toggles[what] = !toggles[what];
  if (!toggles[what]) {
    hideLayer(what);
  } else {
    showLayer(what);
  }
}

function formateDate(time) {
  time *= 1000
  const date = new Date(time);
  return date.toLocaleString(undefined, {
    weekday: 'long',  // e.g., "Friday"
    year: 'numeric',  // e.g., "2024"
    month: 'long',    // e.g., "November"
    day: 'numeric',   // e.g., "8"
    hour: '2-digit',  // e.g., "11" (for "11 AM")
    minute: '2-digit', // e.g., "30"
    second: '2-digit', // e.g., "30"
  });
}

function evtPopupText(evt) {
  return `
    <b>Event</b>: ${evt.event}</br> 
    <b>Time</b>: ${formateDate(evt.time)}</br> 
    <b>Lat</b>: ${evt.gps?.lat}</br>
    <b>Lon</b>: ${(evt.gps?.lon)}</br>`

}

function stopText(evt) {
  return `
    <b>Name</b>: ${(evt.evtdata.fullname)}</br>
    <b>reference</b>: ${(evt.evtdata.reference)}</br>`
}

function triggerText(evt) {
  return `
    <b>Trigger</b>: ${(evt.evtdata.trigger)}</br>`
}

function filterEvents(eventName) {
  const events = dataStore.pidjourneyevents[props.jguid]?.filter(item => item.event === eventName);
  return events?.length ? events : null;
}

function genericEventBuilder(evttype, popupbuilder, markerfn) {
  return (event) => {

    let evtlist = filterEvents(event)

    if (evtlist) {

      // Time Filter
      const f = dataStore.timeFilters[props.jguid];
      if (f && f.enabled) {
        // console.log(`[Overview] Filtering ${evttype}. Range: ${f.start} - ${f.end}`);
        evtlist = evtlist.filter(evt => {
          const t = Number(evt.time);
          return t >= f.start && t <= f.end;
        });
        // console.log(`[Overview] ${evttype} count after filter: ${evtlist.length}`);
      }

      let layer = L.layerGroup();
      let eventCol = dataStore.eventColours[evttype] || 0x000000
      let gpsCoords = [];

      // add markers for the events
      for (let evt of evtlist) {
        let gps = evt.gps
        if (gps?.lat && gps?.lon) {

          gpsCoords.push([gps.lat, gps.lon]);
          let marker
          if (markerfn) {
            marker = markerfn(evt);
          } else {
            marker = L.circleMarker([gps.lat, gps.lon], {
              radius: 12,
              color: 'black',
              fillColor: eventCol,
              fillOpacity: 1
            });
          }

          marker.bindTooltip(popupbuilder(evt), { sticky: false });

          marker.addTo(layer);
        } else {
          console.log(`No GPS data for event [${event}:${evttype}] guid[${evt.guid}] at time [${evt.time}]`)
        }


      }
      return layer;
    }

  }
}

function addBuilders() {

  function announcePopuptext(evt) {
    let str = evtPopupText(evt) + triggerText(evt)
    str += '<pre>' + JSON.stringify(evt.evtdata, null, 2) + '</pre>';
    return str
  }

  function popuptext(evt) {
    return evtPopupText(evt) + stopText(evt)
  }

  function doorsPopuptext(evt) {
    let str = evtPopupText(evt)
    str += '<pre>' + JSON.stringify(evt.evtdata, null, 2) + '</pre>';
    return str
  }

  layerbuilder('announce', genericEventBuilder('announce', announcePopuptext))
  layerbuilder('nextStop', genericEventBuilder('nextStop', popuptext))
  layerbuilder('leftStop', genericEventBuilder('leftStop', popuptext))
  layerbuilder('approachingStop', genericEventBuilder('approachingStop', popuptext))
  layerbuilder('approachingTerminus', genericEventBuilder('approachingTerminus', popuptext))
  layerbuilder('Doors', genericEventBuilder('Doors', doorsPopuptext))
  layerbuilder('atStop', genericEventBuilder('atStop', popuptext))
  layerbuilder('atTerminus', genericEventBuilder('atTerminus', popuptext))
  layerbuilder('departedStop', genericEventBuilder('departedStop', popuptext))
  layerbuilder('pos', genericEventBuilder('pos', evtPopupText, (event) => {

    let gps = event.gps
    let eventCol = dataStore.eventColours['pos'] || 0x000000

    let radius = 8

    return L.circleMarker([gps.lat, gps.lon], {
      radius: radius,
      color: 'black',
      fillColor: eventCol,
      fillOpacity: 1
    });
  }))

}

addBuilders();

</script>

<style>
.event-count-table {
  width: 100%;
  /* Make the table take up 100% of the container width */
  table-layout: auto;
  /* Fix the layout of the table */
  border-collapse: collapse;
}

.event-count-table thead {
  background-color: #007bff;
  /* Set the background color of the header row */
  color: white;
  /* Optional: change the text color to contrast */
}

.event-count-table th,
.event-count-table td {
  border: 1px solid #ccc;
  padding: 0px 10px;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
}

.event-count-table th:nth-child(4),
/* Target the third column for the extra space */
.event-count-table td:nth-child(4) {
  width: 100%;
  /* Let this column take all the remaining space */
}
</style>

<style scoped>
.selected {
  background-color: #f0f0f0;
  /* Highlight color */
}

/* Style for when the button is toggled on */
.toggled {
  background-color: green;
  color: white;
}

/* Default button style */
button {
  background-color: gray;
  color: black;
}
</style>